import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { MobileObserverService } from '@app-services/adaptive/mobile-observer.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { EmailSearchInputComponent } from '../email-search-input/email-search-input.component';
import {
  EMAIL_QUERY_PARAM,
  STORAGE_NAMES,
} from '@app-shared/constants/constants';
import {
  decodeInfoMailAccount,
  encodeInfoMailAccount,
} from '@app-utils/search-params';
import { SearchEvent, SearchInfo } from '@app-types/search';
import { getFolderByType } from '@app-utils/folders';
import { filter } from 'rxjs/operators';
import { MailFolderType } from '@app-types/enums/mail-folder.type';
import { UserWorkspaceService } from '@app-services/user-workspace-service';
import { animate, style, transition, trigger } from '@angular/animations';
import { EmailFolderNameComponent } from '../common/email/email-folder-name/email-folder-name.component';
import { SearchTypeEnum } from '@app-types/enums/filters';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { UserFolderContract } from '@app-types/api/mail-folder';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  MAT_SELECT_CONFIG,
  MatSelectChange,
  MatSelectModule,
} from '@angular/material/select';
import { ROUTES } from '@app-shared/constants/routes/fe.routes';
import {
  IStepOption,
  TourAnchorMatMenuDirective,
  TourService,
} from 'ngx-ui-tour-md-menu';
import { MatBadge } from '@angular/material/badge';
import { LocalStorageService } from '@app-services/local-storage.service';
import { EmailsPageStateService } from '@app-services/emails/emails-page-state.service';
import { Counts } from '@app-types/api/email';
import { MatTooltip } from '@angular/material/tooltip';

const { EMAILS, ACCOUNT_SETTINGS, ORGANIZATION, MAIL_ACCOUNT, ADDRESS_BOOK } =
  ROUTES;
const { PRE_RELEASE } = STORAGE_NAMES;

@Component({
  selector: 'app-main-header',
  imports: [
    MatButtonModule,
    MatIconModule,
    EmailSearchInputComponent,
    EmailFolderNameComponent,
    MatTabsModule,
    TranslateModule,
    MatSelectModule,
    TourAnchorMatMenuDirective,
    MatBadge,
    MatTooltip,
  ],
  templateUrl: './main-header.component.html',
  styleUrls: ['./main-header.component.scss'],
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-out', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ height: '0px', opacity: 0 })),
      ]),
    ]),
  ],
  providers: [
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: 'select-overlay-pane' },
    },
  ],
})
export class MainHeaderComponent implements OnInit, OnDestroy {
  @Output() navMenuOpen = new EventEmitter();
  @Output() mailAccountMenuOpen = new EventEmitter();
  public searchValue = '';
  public useMobileView = false;
  public useSmallDesktopView = false;
  private useMobileViewSubscription: Subscription;
  private useSmallDesktopViewSubscription: Subscription;
  public isDefaultSearchParams = true;

  infoOpenedMailAccount?: SearchInfo;
  public isInbox = true;
  public isAnyFolders = true;
  public isAllFoldersSelected = false;
  public currentFolder?: UserFolderContract;
  public selectedTabIndex: number | null;
  public isVisible = false;
  countBages: { [key: number]: number } = {
    0: 6,
    1: 7,
    2: 9,
  };
  countBadgesKey: Record<string, keyof Counts> = {
    inInboxTabItem: 'newAndValidatedUnreadCount',
    toValidateInboxTabItem: 'notValidatedUnreadCount',
    ReassignedInboxTabItem: 'reassignedUnreadCount',
  };
  private steps: IStepOption[] = [
    {
      anchorId: 'mailAccounts',
      content: this.translate.instant('folderManagement'),
      title: this.translate.instant('mailAccountsBreadCrumbs'),
      enableBackdrop: true,
      placement: {
        horizontal: true,
      },
    },
    {
      anchorId: 'mailAccount-0',
      content: this.translate.instant('mailAccSubContent'),
      title: this.translate.instant('mailAccountLabel'),
      enableBackdrop: true,
      placement: {
        horizontal: true,
      },
    },
    {
      anchorId: 'mailAccountBadge-0',
      content: this.translate.instant('mailAccSubContent'),
      title: 'Badge',
      enableBackdrop: true,
      placement: {
        horizontal: true,
      },
    },
    {
      anchorId: 'emails',
      content: this.translate.instant('emailsContent'),
      title: this.translate.instant('emails'),
      enableBackdrop: true,
      placement: {
        horizontal: true,
      },
    },
    {
      anchorId: 'searchBar',
      content: this.translate.instant('searchBarContent'),
      title: this.translate.instant('searchBar'),
      enableBackdrop: true,
    },
    {
      anchorId: 'create-button',
      content: this.translate.instant('newEmailContent'),
      title: this.translate.instant('newEmail'),
      enableBackdrop: false,
      nextOnAnchorClick: true,
    },
    {
      anchorId: 'newEmailModal',
      content: this.translate.instant('newEmailModalContent'),
      title: this.translate.instant('newEmail'),
      enableBackdrop: false,
      isAsync: true,
      showArrow: false,
      placement: {
        horizontal: true,
      },
    },
  ];
  preRelease = false;
  setInterval: NodeJS.Timeout;

  constructor(
    protected router: Router,
    private mobileObserverService: MobileObserverService,
    public userWorkspaceService: UserWorkspaceService,
    private translate: TranslateService,
    private tourService: TourService,
    private localStorageService: LocalStorageService,
    public emailsPageStateService: EmailsPageStateService
  ) {}

  async ngOnInit(): Promise<any> {
    this.useMobileViewSubscription = this.mobileObserverService
      .mobileObserver()
      .subscribe(isMobile => (this.useMobileView = isMobile));

    this.useSmallDesktopViewSubscription = this.mobileObserverService
      .smallDesktopObserver()
      .subscribe(isSmallDesktop => (this.useSmallDesktopView = isSmallDesktop));

    if (window.location.search) {
      this.setInfoOpenMAilAccount(window.location.href);
    }

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.setInfoOpenMAilAccount(window.location.href);
      });

    this.preRelease = this.localStorageService.getData(PRE_RELEASE);
    this.preRelease && this.setBages();
  }

  setBages(): void {
    this.setInterval = setInterval(() => {
      const key = [0, 1, 2][Math.floor(Math.random() * 3)];
      this.countBages = {
        ...this.countBages,
        [key]: this.countBages[key] >= 100 ? 9 : this.countBages[key] + 1,
      };
    }, 5000);
  }

  toggleVisibility(): void {
    this.isVisible = !this.isVisible;
  }

  resetInfoAcc(): void {
    this.infoOpenedMailAccount = undefined;
    this.isInbox = true;
    this.isAnyFolders = true;
    this.isAllFoldersSelected = false;
    this.currentFolder = undefined;
    this.selectedTabIndex = null;
  }

  setInfoOpenMAilAccount(url: string): void {
    const newUrl = new URL(url);
    if (newUrl.pathname.includes(EMAILS)) {
      const search: string = newUrl.searchParams.get(EMAIL_QUERY_PARAM) ?? '';
      if (search) {
        this.infoOpenedMailAccount = decodeInfoMailAccount(search);
        this.isInbox =
          this.infoOpenedMailAccount?.type === MailFolderType.Inbox;
        this.isAnyFolders = !!this.infoOpenedMailAccount?.folder;
        this.selectedTabIndex = this.isInbox
          ? (this.infoOpenedMailAccount?.tab ?? 0)
          : null;
        this.isAllFoldersSelected =
          this.infoOpenedMailAccount?.searchType === SearchTypeEnum.allFolders;
        this.searchValue = this.infoOpenedMailAccount?.search
          ? this.infoOpenedMailAccount.search
          : '';
        this.userWorkspaceService.hideNotifications =
          this.infoOpenedMailAccount?.hideNotifications !== false;
      } else {
        this.resetInfoAcc();
      }
    } else {
      this.resetInfoAcc();
    }
  }

  selectionChange(event: MatSelectChange): void {
    this.changeTab({ index: event.value } as MatTabChangeEvent);
  }

  changeTab(event: MatTabChangeEvent): void {
    this.selectedTabIndex = event.index;
    const data: SearchInfo = {
      tab: this.selectedTabIndex,
    };

    this.infoOpenedMailAccount?.hideNotifications === false &&
      (data.hideNotifications = this.infoOpenedMailAccount.hideNotifications);
    this.infoOpenedMailAccount?.search &&
      (data.search = this.infoOpenedMailAccount.search);
    this.infoOpenedMailAccount?.searchType &&
      (data.searchType = this.infoOpenedMailAccount.searchType);
    this.infoOpenedMailAccount?.filter &&
      (data.filter = this.infoOpenedMailAccount.filter);

    if (this.infoOpenedMailAccount) {
      const infoMailAcc = encodeInfoMailAccount(
        this.infoOpenedMailAccount.acc as number,
        this.infoOpenedMailAccount.folder as number,
        this.infoOpenedMailAccount.type as MailFolderType,
        data
      );

      this.router.navigate([EMAILS], {
        queryParams: infoMailAcc
          ? {
              [EMAIL_QUERY_PARAM]: infoMailAcc,
            }
          : {},
      });
    }
  }

  public async search($event: SearchEvent): Promise<void> {
    let folder = null;
    if (!this.infoOpenedMailAccount?.acc) {
      folder = getFolderByType(
        this.userWorkspaceService.userFolders,
        this.userWorkspaceService.mailAccounts[0].mailAccountId,
        MailFolderType.Inbox
      );
    }
    const nullableSearchValue =
      this.searchValue?.length === 0 ? '' : this.searchValue;
    const data: SearchInfo = {};
    this.userWorkspaceService.hideNotifications === false &&
      (data.hideNotifications = this.userWorkspaceService.hideNotifications);
    !!nullableSearchValue && (data.search = nullableSearchValue);

    if ($event?.hideNotificationsEmitter) {
      if (this.infoOpenedMailAccount?.filter) {
        data.filter = this.infoOpenedMailAccount.filter;
        this.infoOpenedMailAccount?.searchType &&
          (data.searchType = this.infoOpenedMailAccount.searchType);
      }
    } else {
      $event?.searchType && (data.searchType = $event.searchType);
      $event?.filter && (data.filter = $event.filter);
    }

    (!!this.infoOpenedMailAccount?.tab ||
      this.infoOpenedMailAccount?.tab === 0) &&
      (data.tab = this.infoOpenedMailAccount.tab);

    const infoMailAcc = encodeInfoMailAccount(
      this.infoOpenedMailAccount?.acc ??
        this.userWorkspaceService.mailAccounts[0].mailAccountId,
      this.infoOpenedMailAccount?.folder ?? folder ?? 0,
      this.infoOpenedMailAccount?.type ?? MailFolderType.Inbox,
      data
    );

    this.router.navigate([EMAILS], {
      queryParams: infoMailAcc
        ? {
            [EMAIL_QUERY_PARAM]: infoMailAcc,
          }
        : {},
    });
  }

  public leftMenuClicked(): void {
    this.mailAccountMenuOpen.emit();
  }

  public rightMenuClicked(): void {
    this.navMenuOpen.emit();
  }

  public goToProfile(): void {
    this.router.navigateByUrl(`/${ACCOUNT_SETTINGS}`);
  }

  public goToAddressBook(): void {
    this.router.navigateByUrl(
      `/${ORGANIZATION}/${MAIL_ACCOUNT}/${this.infoOpenedMailAccount?.acc ?? this.userWorkspaceService.mailAccounts[0].mailAccountId}/${ADDRESS_BOOK}`
    );
  }

  public onSearchValueChanged(newSearchValue: string): void {
    this.searchValue = newSearchValue;
  }

  public onHideNotificationsChanged(hideNotifications: boolean): void {
    this.userWorkspaceService.hideNotifications = hideNotifications;
    this.infoOpenedMailAccount?.acc &&
      this.userWorkspaceService.getBadgesForMailAccounts([
        this.infoOpenedMailAccount.acc,
      ]);
    this.userWorkspaceService.refreshUnread({
      mailAccountId: (this.infoOpenedMailAccount?.acc ??
        this.userWorkspaceService.mailAccounts[0].mailAccountId) as number,
      updateBadges: false,
    });
  }

  isVisibleFolderName(): boolean {
    return (
      window.location.href.includes(EMAILS) &&
      !!this.userWorkspaceService.mailAccounts.length
    );
  }

  startTour(): void {
    !this.tourService.steps.length &&
      this.tourService.initialize(this.steps, {
        enableBackdrop: true,
        backdropConfig: {
          offset: 10,
        },
      });
    this.tourService.start();
  }

  async ngOnDestroy(): Promise<void> {
    this.useMobileViewSubscription?.unsubscribe();
    this.useSmallDesktopViewSubscription?.unsubscribe();
    this.setInterval && clearInterval(this.setInterval);
    this.tourService?.end();
  }
}
